import { useTwilioCall } from './twilio-device-provider';
import { Phone, PhoneOff, Mic, MicOff, Hash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

const statusLabels: Record<string, string> = {
  connecting: 'Connecting…',
  ringing: 'Ringing…',
  connected: 'Connected',
  disconnected: 'Call ended',
};

export function ActiveCallWidget() {
  const {
    callState,
    activeCallInfo,
    callDuration,
    isMuted,
    hangUp,
    toggleMute,
    sendDigits,
  } = useTwilioCall();

  const [showKeypad, setShowKeypad] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  // Handle exit animation
  useEffect(() => {
    if (callState === 'idle' || callState === 'disconnected') {
      if (activeCallInfo) {
        setIsExiting(true);
        const t = setTimeout(() => setIsExiting(false), 300);
        return () => clearTimeout(t);
      }
    }
  }, [callState, activeCallInfo]);

  if (callState === 'idle' && !isExiting) return null;

  const isActive = callState !== 'idle' && callState !== 'disconnected';
  const displayName = activeCallInfo?.contactName || activeCallInfo?.to || 'Unknown';

  const keypadButtons = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

  return (
    <div
      className={`
        fixed bottom-0 left-0 right-0 z-[100]
        transition-all duration-300 ease-in-out
        ${isActive ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
      `}
    >
      {/* Keypad overlay */}
      {showKeypad && (
        <div className="mx-auto max-w-lg px-4 pb-2">
          <div className="bg-gray-900/95 backdrop-blur-xl rounded-t-2xl border border-gray-700/50 p-4 shadow-2xl">
            <div className="grid grid-cols-3 gap-2">
              {keypadButtons.map((digit) => (
                <button
                  key={digit}
                  onClick={() => sendDigits(digit)}
                  className="
                    h-12 rounded-lg bg-gray-800/80 hover:bg-gray-700/80
                    text-white text-lg font-medium
                    transition-all duration-150 active:scale-95
                    border border-gray-600/30
                  "
                >
                  {digit}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main call bar */}
      <div className="bg-gray-900/95 backdrop-blur-xl border-t border-gray-700/50 shadow-2xl">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Left: Call info */}
            <div className="flex items-center gap-3 min-w-0">
              <div className={`
                w-10 h-10 rounded-full flex items-center justify-center
                ${callState === 'connected'
                  ? 'bg-emerald-500/20 text-emerald-400'
                  : 'bg-blue-500/20 text-blue-400'
                }
              `}>
                <Phone className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-white font-medium truncate text-sm">
                  {displayName}
                </p>
                <div className="flex items-center gap-2">
                  <span className={`
                    text-xs
                    ${callState === 'connected' ? 'text-emerald-400' : 'text-blue-400'}
                  `}>
                    {statusLabels[callState] || callState}
                  </span>
                  {callState === 'connected' && (
                    <span className="text-xs text-muted-foreground font-mono">
                      {formatTime(callDuration)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Right: Controls */}
            <div className="flex items-center gap-2">
              {/* Mute button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className={`
                  rounded-full h-10 w-10
                  ${isMuted
                    ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30 hover:text-red-300'
                    : 'bg-gray-700/50 text-muted-foreground hover:bg-gray-600/50 hover:text-white'
                  }
                `}
                title={isMuted ? 'Unmute' : 'Mute'}
              >
                {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>

              {/* Keypad button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowKeypad(!showKeypad)}
                className={`
                  rounded-full h-10 w-10
                  ${showKeypad
                    ? 'bg-blue-500/20 text-blue-400 hover:bg-blue-500/30'
                    : 'bg-gray-700/50 text-muted-foreground hover:bg-gray-600/50 hover:text-white'
                  }
                `}
                title="Keypad"
              >
                <Hash className="h-4 w-4" />
              </Button>

              {/* Hang up button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={hangUp}
                className="
                  rounded-full h-10 w-10
                  bg-red-600 text-white hover:bg-red-500 hover:text-white
                  transition-all duration-150 active:scale-95
                "
                title="Hang up"
              >
                <PhoneOff className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Pulse animation for connecting/ringing */}
      {(callState === 'connecting' || callState === 'ringing') && (
        <div className="absolute top-0 left-0 right-0 h-0.5">
          <div className="h-full bg-blue-500 animate-pulse" />
        </div>
      )}
    </div>
  );
}
